import { z } from "zod";

/**
 * The brand-voice vocabulary itself: how a rule, a lexicon entry, an example
 * or a visual guideline is classified, how strongly it binds, and what scope
 * it applies to. Version and source lifecycle live in `tov`.
 */

export const TOV_BOUNDARY_TYPES = [
  "sounds_like",
  "not_like",
  "do",
  "avoid",
] as const;
export const TovBoundaryTypeSchema = z.enum(TOV_BOUNDARY_TYPES);
export type TovBoundaryType = z.infer<typeof TovBoundaryTypeSchema>;

export const TOV_RULE_TYPES = [
  "required",
  "preferred",
  "allowed",
  "avoid",
  "forbidden",
] as const;
export const TovRuleTypeSchema = z.enum(TOV_RULE_TYPES);
export type TovRuleType = z.infer<typeof TovRuleTypeSchema>;

export const TOV_RULE_SEVERITIES = [
  "blocking",
  "major",
  "minor",
  "advisory",
] as const;
export const TovRuleSeveritySchema = z.enum(TOV_RULE_SEVERITIES);
export type TovRuleSeverity = z.infer<typeof TovRuleSeveritySchema>;

export const TOV_SCOPE_TYPES = [
  "global",
  "channel",
  "journey_stage",
  "audience",
  "product",
  "page_type",
  "locale",
] as const;
export const TOV_SCOPE_TYPE = {
  GLOBAL: "global",
  CHANNEL: "channel",
  JOURNEY_STAGE: "journey_stage",
  AUDIENCE: "audience",
  PRODUCT: "product",
  PAGE_TYPE: "page_type",
  LOCALE: "locale",
} as const satisfies Record<string, TovScopeType>;
export const TovScopeTypeSchema = z.enum(TOV_SCOPE_TYPES);
export type TovScopeType = z.infer<typeof TovScopeTypeSchema>;

export const TOV_LEXICON_TYPES = [
  "prefer",
  "avoid",
  "forbidden",
  "replacement",
] as const;
export const TOV_LEXICON_TYPE = {
  PREFER: "prefer",
  AVOID: "avoid",
  FORBIDDEN: "forbidden",
  REPLACEMENT: "replacement",
} as const satisfies Record<string, TovLexiconType>;
export const TovLexiconTypeSchema = z.enum(TOV_LEXICON_TYPES);
export type TovLexiconType = z.infer<typeof TovLexiconTypeSchema>;

export const TOV_EXAMPLE_TYPES = [
  "on_brand",
  "off_brand",
  "rewrite_pair",
  "reference",
] as const;
export const TovExampleTypeSchema = z.enum(TOV_EXAMPLE_TYPES);
export type TovExampleType = z.infer<typeof TovExampleTypeSchema>;

export const TOV_VISUAL_RULE_TYPES = [
  "imagery",
  "layout",
  "color",
  "typography",
  "iconography",
  "motion",
  "alt_text",
] as const;
export const TovVisualRuleTypeSchema = z.enum(TOV_VISUAL_RULE_TYPES);
export type TovVisualRuleType = z.infer<typeof TovVisualRuleTypeSchema>;

export const TOV_CHANNEL_ROUTE_TYPES = [
  "traffic_source",
  "traffic_destination",
] as const;
export const TovChannelRouteTypeSchema = z.enum(TOV_CHANNEL_ROUTE_TYPES);
export type TovChannelRouteType = z.infer<typeof TovChannelRouteTypeSchema>;
